import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import {
    ActivityHistoryCard,
    useModal,
    type ActivityFilterProps,
    type ActivityProps,
} from '@europrocurement/flexy-components';
import { useFormaliteService, Utilisateur } from '@europrocurement/l2d-domain';
import { ModalCreateNote } from '@europrocurement/flexy-components/components/organisms/ModalCreateNote';
import { ConfirmationModal } from '@europrocurement/flexy-components/components/organisms/ConfirmationModal';
import { ModalEditNote } from '@europrocurement/flexy-components/components/organisms/ModalEditNote';
import { useSnackbar } from 'notistack';
import { FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { SelectedDossierContext } from '../../../providers';

const NOTE_DELETE_MESSAGES = {
    question: 'Supprimer cette note ?',
    detail: {
        message: 'Cette action est irréversible. La note sera définitivement supprimée.',
        severity: 'warning' as const,
    },
    validation: 'Supprimer',
    annulation: 'Annuler',
};

interface UserCache {
    [email: string]: Utilisateur | null;
}

interface ActivityTabProps {
    /** Fonction pour récupérer un utilisateur par email */
    getUserByEmail: (email: string) => Promise<Utilisateur>;
}

/**
 * Formate le nom et prénom d'un utilisateur selon les conventions d'affichage
 */
const formatUserName = (prenom?: string, nom?: string): { firstName: string; lastName: string } => {
    if (!prenom || !nom) {
        return { firstName: '', lastName: '' };
    }

    return {
        firstName: prenom.charAt(0).toUpperCase() + prenom.slice(1).toLowerCase(),
        lastName: nom.toUpperCase(),
    };
};

/**
 * Onglet d'activités pour un dossier
 * Affiche les notes et actions liées au dossier sélectionné
 */
const ActivityTab: React.FunctionComponent<ActivityTabProps> = function ({ getUserByEmail }) {
    const { dossier, dossierWithNotes, refreshDossierWithNotes, notesLoading, notesError } =
        useContext(SelectedDossierContext);

    const [activities, setActivities] = useState<ActivityProps[]>([]);
    const [activitiesLoading, setActivitiesLoading] = useState(true);
    const [isEditingSaving, setIsEditingSaving] = useState(false);
    const [isDeletingSaving, setIsDeletingSaving] = useState(false);
    const [userCache, setUserCache] = useState<UserCache>({});

    const { dossierApi } = useFormaliteService();
    const { modalActions } = useModal();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();

    /**
     * Résout les informations utilisateur à partir d'un email avec mise en cache
     */
    const resolveUserInfo = useCallback(
        async (email: string): Promise<Utilisateur | null> => {
            if (userCache[email] !== undefined) {
                return userCache[email];
            }

            try {
                const user = await getUserByEmail(email);
                setUserCache((prev) => ({ ...prev, [email]: user }));
                return user;
            } catch {
                setUserCache((prev) => ({ ...prev, [email]: null }));
                return null;
            }
        },
        [getUserByEmail, userCache],
    );

    /**
     * Convertit une note de dossier en activité avec résolution des informations utilisateur
     */
    const createNoteActivity = useCallback(
        async (
            note: FproDossierNoteDossierReadNoteGroupTimestampableBlameambleGroup,
        ): Promise<ActivityProps> => {
            const createdByUser = note.createdBy ? await resolveUserInfo(note.createdBy) : null;
            const updatedByUser = note.updatedBy ? await resolveUserInfo(note.updatedBy) : null;

            const createdByFormatted = createdByUser
                ? formatUserName(createdByUser.prenom, createdByUser.nom)
                : { firstName: note.createdBy || 'Anonyme', lastName: '' };

            const updatedByFormatted = updatedByUser
                ? formatUserName(updatedByUser.prenom, updatedByUser.nom)
                : { firstName: note.updatedBy || '', lastName: '' };

            // TODO: Il faudra changer ça par le keycloakUserId une fois que les dernières mises à jour seront merge. En attendant, on utilise le preferred_username comme identifiant pour récupérer l'avatar.
            const userId = createdByUser?.user || note.createdBy || 'unknown';

            return {
                id: `note-${note.id}`,
                type: 'note',
                noteType: note.highlighted ? 'important' : 'standard',
                content: note.content || '',
                createdAt: note.createdAt ? new Date(note.createdAt) : new Date(),
                userId: typeof userId === 'string' ? userId : String(userId),
                memoClient: note.memoClient || false,
                userFirstName: createdByFormatted.firstName,
                userLastName: createdByFormatted.lastName,
                updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
                updatedBy: note.updatedBy || undefined,
                updatedByFirstName: updatedByFormatted.firstName,
                updatedByLastName: updatedByFormatted.lastName,
            };
        },
        [resolveUserInfo],
    );

    useEffect(() => {
        const loadActivities = async () => {
            const realNoteActivities: ActivityProps[] = await Promise.all(
                (dossierWithNotes.notes || []).map(createNoteActivity),
            );

            const allActivities = [...realNoteActivities].sort(
                (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
            );

            setActivities(allActivities);
            setActivitiesLoading(false);
        };

        if (!notesLoading) {
            loadActivities();
        }
    }, [createNoteActivity, dossierWithNotes.notes, notesLoading]);

    /**
     * Gère l'ajout d'une nouvelle note
     */
    const handleAddNote = () => {
        modalActions.call(
            <ModalCreateNote
                onConfirm={async (content: string, highlighted: boolean, memoClient: boolean) => {
                    if (!dossier?.id) return;

                    try {
                        await dossierApi.apiDossiersIdNotesPost({
                            id: dossier?.id.toString(),
                            apiDossiersIdNotesPostRequest: {
                                content,
                                highlighted,
                                memoClient,
                            },
                        });

                        await refreshDossierWithNotes();
                        enqueueSnackbar(`Note créée avec succès`);
                    } catch (error: unknown) {
                        console.error('Erreur lors de la création de la note:', error);
                        enqueueSnackbar(`Erreur lors de la création de la note`, {
                            variant: 'error',
                        });
                    }
                }}
            />,
        );
    };

    /**
     * Gère l'édition d'une note existante
     */
    const handleEditNote = (
        noteId: string,
        content: string,
        highlighted: boolean,
        memoClient: boolean,
    ) => {
        modalActions.call(
            <ModalEditNote
                initialContent={content}
                initialHighlighted={highlighted}
                initialMemoClient={memoClient}
                isSaving={isEditingSaving}
                onConfirm={async (
                    newContent: string,
                    newHighlighted: boolean,
                    newMemoClient: boolean,
                ) => {
                    if (!dossier?.id) return;

                    try {
                        setIsEditingSaving(true);

                        await dossierApi.apiDossiersIdNotesNoteIdPut({
                            id: dossier?.id,
                            noteId: +noteId.replace('note-', ''),
                            apiDossiersIdNotesPostRequest: {
                                content: newContent,
                                highlighted: newHighlighted,
                                memoClient: newMemoClient,
                            },
                        });

                        await refreshDossierWithNotes();
                        modalActions.close();
                        enqueueSnackbar(`Note mise à jour avec succès`);
                    } catch (error) {
                        console.error('Erreur lors de la modification de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            ('response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(`Vous n'avez pas les droits pour modifier cette note`, {
                                variant: 'error',
                            });
                        } else {
                            enqueueSnackbar(`Erreur lors de la modification de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setIsEditingSaving(false);
                    }
                }}
                onCancel={() => modalActions.close()}
            />,
        );
    };

    /**
     * Gère la suppression d'une note
     */
    const handleDeleteNote = (noteId: string) => {
        modalActions.call(
            <ConfirmationModal
                messages={NOTE_DELETE_MESSAGES}
                color={theme.palette.error}
                disabled={isDeletingSaving}
                actionOnValidation={async () => {
                    if (!dossier?.id) return;

                    try {
                        setIsDeletingSaving(true);

                        await dossierApi.apiDossiersIdNotesNoteIdDelete({
                            id: dossier?.id,
                            noteId: +noteId.replace('note-', ''),
                        });

                        await refreshDossierWithNotes();
                        modalActions.close();
                        enqueueSnackbar(`Note supprimée avec succès`);
                    } catch (error) {
                        console.error('Erreur lors de la suppression de la note:', error);

                        const isUnauthorized =
                            (error &&
                                typeof error === 'object' &&
                                'status' in error &&
                                error.status === 403) ||
                            ('response' in error &&
                                error.response &&
                                typeof error.response === 'object' &&
                                'status' in error.response &&
                                error.response.status === 403);

                        if (isUnauthorized) {
                            enqueueSnackbar(
                                `Vous n'avez pas les droits pour supprimer cette note`,
                                {
                                    variant: 'error',
                                },
                            );
                        } else {
                            enqueueSnackbar(`Erreur lors de la suppression de la note`, {
                                variant: 'error',
                            });
                        }
                    } finally {
                        setIsDeletingSaving(false);
                    }
                }}
                actionOnCancellation={() => modalActions.close()}
            />,
        );
    };

    /**
     * Gère les changements de filtre
     */
    const handleFilterChange = (filters: ActivityFilterProps[]) => {
        console.log('Filtres appliqués:', filters);
    };

    return (
        <ActivityHistoryCard
            activities={activities}
            onAddNote={handleAddNote}
            onEditNote={handleEditNote}
            onDeleteNote={handleDeleteNote}
            onFilterChange={handleFilterChange}
            activitiesLoading={activitiesLoading}
            notesLoading={notesLoading}
            notesError={notesError}
        />
    );
};

export default ActivityTab;
